import Styled from '@/components/heros/SimpleHero.styled';
import InlineMutedVideo from '@/components/InlineMutedVideo';
import { PlayButton } from '@/components/pages/frontlines/components/event/styled.components';
import { HubspotFieldNames } from '@/constants/hubspot';
import { GlobalContext } from '@/context';
import { UserContext } from '@/context/user';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { addElementIdentifier } from '@/lib/utils';
import { GRADIENTS } from '@/styles/color';
import { getBrandColor, hexToRGBA } from '@/styles/color/utils';
import { FC, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export interface SimpleHeroProps extends PropsWithChildren, PropsWithClassName {
  eyebrow?: string;
  heading: string;
  background?: string;
  asset?: ReactNode;
  ctas?: ({ isInverted }) => ReactNode;
}

export interface SimpleHeroGroupedInputProps {
  buttonText: string;
  buttonOnClick?: () => void;
}

export interface VideoWrapperProps {
  src: string;
  width?: number;
  height?: number;
}

type SimpleHero = FC<SimpleHeroProps> & {
  GroupedInput: FC<SimpleHeroGroupedInputProps>;
  VideoWrapper: FC<VideoWrapperProps>;
};

const SimpleHero: SimpleHero = (props) => {
  const { toggleInvertedNav } = useContext(GlobalContext);
  const isInverted = useMemo(() => props.background === 'dark', [props.background]);

  useEffect(() => {
    if (isInverted) {
      toggleInvertedNav(true);

      return () => {
        toggleInvertedNav(false);
      };
    }
  }, [isInverted]);

  const [background, fade] = useMemo(() => {
    let background: string | undefined;
    let fade: boolean = false;

    if (isInverted) {
      background = GRADIENTS.DARK;
    }

    if (props.background) {
      if (props.background === 'transparent') {
        background = props.background;
      }

      if (props.background.startsWith('gradient')) {
        background = `url('/staticwebassets/modules/simplehero/background-${props.background}.jpg')`;
      }

      const brandColor = getBrandColor(props.background, 'tint');

      if (brandColor) {
        fade = true;

        background = `linear-gradient(0deg, ${hexToRGBA(brandColor, 0)} 0%, ${hexToRGBA(brandColor, 1)} 75%)`;
      }
    }

    return [background, fade];
  }, [props.background, isInverted]);

  const ctas = props.ctas ? props.ctas({ isInverted }) : null;

  return (
    /*
     * Need a $ prefix for non-string styled component props otherwise we get a console error
     * See https://styled-components.com/docs/api#transient-props
     */
    <Styled.Hero
      $background={background}
      $fade={fade}
      $inverted={isInverted}
      className={props.className}
      {...addElementIdentifier('simple-hero')}
    >
      <Styled.Container>
        {/*
         * Need a $ prefix for camelCase styled component props otherwise we get a console error
         * See https://styled-components.com/docs/api#transient-props
         */}
        <Styled.LeftColumn $hasAsset={!!props.asset}>
          <Styled.Heading>
            {props.eyebrow && <Styled.Eyebrow id="hero-eyebrow">{props.eyebrow}</Styled.Eyebrow>}
            <h1 id="hero-heading">{props.heading}</h1>
          </Styled.Heading>

          <Styled.TextContainer id="hero-text">{props.children}</Styled.TextContainer>

          {ctas ? <Styled.CTAWrapper>{ctas}</Styled.CTAWrapper> : null}
        </Styled.LeftColumn>

        {props.asset ? (
          <Styled.RightColumn>
            <Styled.AssetWrapper>{props.asset}</Styled.AssetWrapper>
          </Styled.RightColumn>
        ) : null}
      </Styled.Container>
    </Styled.Hero>
  );
};

const GroupedInput: FC<SimpleHeroGroupedInputProps> = ({ buttonText, buttonOnClick }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const { userData, setUserData } = useContext(UserContext);
  const [currentBreakpoint] = useBreakpoint();

  return (
    <Styled.StyledGroupedInput
      name={HubspotFieldNames.Email}
      type="email"
      value={userData[HubspotFieldNames.Email]}
      placeholder="Your email address"
      submitText={buttonText ?? 'Get started'}
      split={currentBreakpoint === 'mobile'}
      onValueChange={(email) => setUserData({ [HubspotFieldNames.Email]: email })}
      onSubmit={() => {
        toggleGetStartedModal(true);
        if (buttonOnClick) buttonOnClick();

        gtm.track('submit_form', {
          location: 'Hero',
          form_name: 'Email Capture',
        });
      }}
      onButtonClick={() => {
        gtm.track('click', {
          element_type: 'button',
          location: 'Hero',
          element_label: buttonText ?? 'Get started',
        });
      }}
    />
  );
};

GroupedInput.displayName = 'SimpleHeroGroupedInput';

SimpleHero.GroupedInput = GroupedInput;

const VideoWrapper: FC<VideoWrapperProps> = (props) => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  return (
    <>
      <Styled.VideoWrapper
        key="video"
        onClick={() => {
          setShowVideoModal(true);

          gtm.track('click', {
            location: 'Hero',
            element_type: 'button',
            element_label: 'Hero Video',
            video_url: props.src,
          });
        }}
      >
        <PlayButton />
        <InlineMutedVideo autoPlay muted playsInline loop width={props.width} height={props.height}>
          <source src={props.src} />
        </InlineMutedVideo>
      </Styled.VideoWrapper>

      {showVideoModal && (
        <Styled.VideoModal
          name="Hero Video"
          whiteCloseButton
          onClose={() => {
            setShowVideoModal(false);
          }}
        >
          <Styled.ModalVideoWrapper>
            <video
              autoPlay
              controls
              onEnded={() => {
                setShowVideoModal(false);
              }}
            >
              <source src={props.src} />
            </video>
          </Styled.ModalVideoWrapper>
        </Styled.VideoModal>
      )}
    </>
  );
};

VideoWrapper.displayName = 'SimpleHeroVideoWrapper';

SimpleHero.VideoWrapper = VideoWrapper;

export default SimpleHero;
